import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const sharedSpaces = [
  {
    id: 1,
    name: 'Startup Sidekick',
    description: 'Your productivity co-pilot for ambitious projects.',
    interests: ['Technology', 'Productivity'],
    roles: ['Professional Assistant', 'Mentor / Coach'],
    popularity: 95,
    createdAt: '2024-05-01',
    controversialScore: 10,
    likes: 152,
    dislikes: 9,
  },
  {
    id: 2,
    name: 'Creative Spark',
    description: 'Brainstorm wild ideas and write with flair.',
    interests: ['Art', 'Writing', 'Music'],
    roles: ['Creative Collaborator', 'Idea Validator'],
    popularity: 75,
    createdAt: '2024-04-20',
    controversialScore: 30,
    likes: 128,
    dislikes: 6,
  },
  {
    id: 3,
    name: 'Mindful Moments',
    description: 'A calming companion for stress relief and reflection.',
    interests: ['Nature', 'Wellness'],
    roles: ['Wellness Companion', 'Social Buddy/Friend'],
    popularity: 80,
    createdAt: '2024-04-10',
    controversialScore: 5,
    likes: 180,
    dislikes: 3,
  },
];

const sortOptions = ['A–Z', 'Z–A', 'Most Popular', 'Newest', 'Oldest', 'Most Controversial'];

function BrowseSpace() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('A–Z');
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = (space) => {
    setSelectedSpace(space);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedSpace(null);
    setOpenModal(false);
  };

  const handleRandomSpace = () => {
    const random = sharedSpaces[Math.floor(Math.random() * sharedSpaces.length)];
    handleOpenModal(random);
  };

  const filteredSpaces = sharedSpaces
    .filter((space) =>
      space.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortOption) {
        case 'A–Z':
          return a.name.localeCompare(b.name);
        case 'Z–A':
          return b.name.localeCompare(a.name);
        case 'Most Popular':
          return b.popularity - a.popularity;
        case 'Newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'Oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'Most Controversial':
          return b.controversialScore - a.controversialScore;
        default:
          return 0;
      }
    });

  return (
    <Box maxWidth="lg" mx="auto" p={4}>
      <Typography variant="h4" gutterBottom>
        MyspAIce
      </Typography>
      <Typography variant="subtitle1" mb={2}>
        Discover spaces that others have created and shared. Find inspiration or just explore.
      </Typography>

      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        <TextField
          variant="outlined"
          placeholder="Search spaces..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: '240px' }}
        />

        <TextField
          select
          label="Sort By"
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          sx={{ minWidth: '200px' }}
        >
          {sortOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="outlined" color="info" onClick={handleRandomSpace}>
          Random Space
        </Button>

        <Button variant="contained" color="secondary">
          Create Your Own Space
        </Button>
      </Box>

      <Grid container spacing={3}>
        {filteredSpaces.map((space) => (
          <Grid item xs={12} sm={6} md={4} key={space.id}>
            <Card elevation={4}>
              <CardContent sx={{ position: 'relative' }}>
              <Box position="absolute" top={8} right={8} textAlign="right" sx={{ lineHeight: 1.5, mb: 2 }}>

                  <Typography variant="body2">❤️ {space.likes}</Typography>
                  <Typography variant="body2">👎 {space.dislikes}</Typography>
                </Box>

                <Typography variant="h6" gutterBottom>
                  {space.name}
                </Typography>
                <Typography variant="body2" mb={2}>
                  {space.description}
                </Typography>

                <Typography variant="subtitle2">Interests:</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                  {space.interests.map((interest, idx) => (
                    <Chip key={idx} label={interest} size="small" color="default" />
                  ))}
                </Stack>

                <Typography variant="subtitle2">AI Roles:</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {space.roles.map((role, idx) => (
                    <Chip key={idx} label={role} size="small" color="primary" />
                  ))}
                </Stack>
              </CardContent>

              <CardActions>
                <Button fullWidth variant="outlined" onClick={() => handleOpenModal(space)}>
                  View Details
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSpace?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            {selectedSpace?.description}
          </Typography>

          <Typography variant="subtitle2">Interests:</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
            {selectedSpace?.interests.map((interest, idx) => (
              <Chip key={idx} label={interest} size="small" color="default" />
            ))}
          </Stack>

          <Typography variant="subtitle2">AI Roles:</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {selectedSpace?.roles.map((role, idx) => (
              <Chip key={idx} label={role} size="small" color="primary" />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BrowseSpace;
